/**
 * Cross-component, in-memory store of deal field edits keyed by deal id.
 * Mirrors {@link accountEditStore} so edits made in the grid and drill-down
 * sheet stay in sync within the same browser session.
 *
 * Preview/demo-grade state; not persisted.
 */

export interface DealOverride {
  /** Full owner list, primary first. */
  owners?: string[];
  /** Full contact-name list, primary first. */
  contacts?: string[];
}

const store = new Map<string, DealOverride>();
const listeners = new Set<() => void>();
let version = 0;

const notify = () => {
  version += 1;
  for (const l of listeners) l();
};

export const dealEditStore = {
  get(id: string | undefined | null): DealOverride | undefined {
    if (!id) return undefined;
    return store.get(id);
  },
  set(id: string, override: DealOverride): void {
    if (!id) return;
    store.set(id, override);
    notify();
  },
  merge(id: string, partial: DealOverride): DealOverride {
    if (!id) return partial;
    const next = { ...(store.get(id) || {}), ...partial };
    store.set(id, next);
    notify();
    return next;
  },
  clear(id: string): void {
    if (!id) return;
    store.delete(id);
    notify();
  },
  getSnapshot(): number {
    return version;
  },
  subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
};